import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { X, User, Mail, Shield, AlertCircle, CheckCircle, Clock, Building2, Power, RefreshCw, Trash2, Lock } from 'lucide-react';
import { userManageService } from '../../services';
import { useAuth } from '../../context';
import { PERMISSIONS } from '../../constants';
import './ManageUserDrawer.css';

const ManageUserDrawer = ({ 
  isOpen, 
  onClose, 
  user, 
  onSaveRole, 
  onSuspendUser,
  onReactivateUser,
  onDeactivateUser
}) => {
  const { hasPermission } = useAuth();

  // ✅ Permission checks
  const canAssignRole = hasPermission(PERMISSIONS.USERS.ROLE_ASSIGN);
  const canSuspend = hasPermission(PERMISSIONS.USERS.SUSPEND);
  const canDelete = hasPermission(PERMISSIONS.USERS.DELETE);

  // ✅ Check if user can do anything at all
  const canDoAnything = canAssignRole || canSuspend || canDelete;

  const [selectedRole, setSelectedRole] = useState('');
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [isSuspending, setIsSuspending] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Refs to prevent double API calls
  const isSavingRef = useRef(false);
  const isSuspendingRef = useRef(false);
  const isReactivatingRef = useRef(false);
  const isDeactivatingRef = useRef(false);

  // Reset all state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setIsSuspending(false);
      setIsReactivating(false);
      setIsDeactivating(false);
      setIsSaving(false);
      setError('');
      setSuccess('');
      isSavingRef.current = false;
      isSuspendingRef.current = false;
      isReactivatingRef.current = false;
      isDeactivatingRef.current = false;
    }
  }, [isOpen]);

  // Reset refs when user changes
  useEffect(() => {
    if (user && isOpen) {
      isSavingRef.current = false;
      isSuspendingRef.current = false;
      isReactivatingRef.current = false;
      isDeactivatingRef.current = false;
      setError('');
      setSuccess('');
    }
  }, [user, isOpen]);

  // Load roles only if user can assign roles
  useEffect(() => {
    if (isOpen && user && canAssignRole) {
      loadRoles();
      setSelectedRole(user.role?.uuid || '');
    }
  }, [isOpen, user, canAssignRole]);

  const loadRoles = async () => {
    setLoadingRoles(true);
    const result = await userManageService.getRoles();
    if (result.success) {
      setRoles(result.data || []);
    } else {
      setError('Failed to load roles');
    }
    setLoadingRoles(false);
  };

  const handleSaveRole = async () => {
    if (!canAssignRole) return;
    if (isSavingRef.current) return;
    if (!user) return;
    if (!selectedRole) {
      setError('Please select a role');
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const result = await userManageService.updateUserRole(user.uuid, selectedRole);
      if (result.success) {
        setSuccess(result.message || 'Role updated successfully');
        const newRoleName = roles.find(r => r.uuid === selectedRole)?.name || selectedRole;
        onSaveRole(user.uuid, newRoleName);
        
        setIsSaving(false);
        isSavingRef.current = false;
        
        setTimeout(() => {
          onClose();
        }, 5000);
      } else {
        setError(result.message || 'Failed to update role');
        setIsSaving(false);
        isSavingRef.current = false;
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
      setIsSaving(false);
      isSavingRef.current = false;
    }
  };

  const handleSuspend = async () => {
    if (!canSuspend) return;
    if (isSuspendingRef.current) return;
    if (!user) return;

    isSuspendingRef.current = true;
    setIsSuspending(true);
    setError('');
    setSuccess('');

    try {
      const result = await userManageService.suspendUser(user.uuid);
      if (result.success) {
        setSuccess(result.message || 'User suspended successfully');
        onSuspendUser(user.uuid);
        
        setIsSuspending(false);
        isSuspendingRef.current = false;
        setShowSuspendModal(false);
        
        setTimeout(() => {
          onClose();
        }, 5000);
      } else {
        setError(result.message || 'Failed to suspend user');
        setIsSuspending(false);
        isSuspendingRef.current = false;
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
      setIsSuspending(false);
      isSuspendingRef.current = false;
    }
  };

  const handleReactivate = async () => {
    if (!canSuspend) return;
    if (isReactivatingRef.current) return;
    if (!user) return;

    isReactivatingRef.current = true;
    setIsReactivating(true);
    setError('');
    setSuccess('');

    try {
      const result = await userManageService.reactivateUser(user.uuid);
      if (result.success) {
        setSuccess(result.message || 'User reactivated successfully');
        onReactivateUser(user.uuid);
        
        setIsReactivating(false);
        isReactivatingRef.current = false;
        setShowReactivateModal(false);
        
        setTimeout(() => {
          onClose();
        }, 5000);
      } else {
        setError(result.message || 'Failed to reactivate user');
        setIsReactivating(false);
        isReactivatingRef.current = false;
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
      setIsReactivating(false);
      isReactivatingRef.current = false;
    }
  };

  const handleDeactivate = async () => {
    if (!canDelete) return;
    if (isDeactivatingRef.current) return;
    if (!user) return;

    isDeactivatingRef.current = true;
    setIsDeactivating(true);
    setError('');
    setSuccess('');

    try {
      const result = await userManageService.deleteUser(user.uuid);
      if (result.success) {
        setSuccess(result.message || 'User deactivated successfully');
        onDeactivateUser(user.uuid);
        
        setIsDeactivating(false);
        isDeactivatingRef.current = false;
        setShowDeactivateModal(false);
        
        setTimeout(() => {
          onClose();
        }, 5000);
      } else {
        setError(result.message || 'Failed to deactivate user');
        setIsDeactivating(false);
        isDeactivatingRef.current = false;
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
      setIsDeactivating(false);
      isDeactivatingRef.current = false;
    }
  };

  const handleModalClose = (modalSetter) => {
    modalSetter(false);
    setIsSuspending(false);
    setIsReactivating(false);
    setIsDeactivating(false);
    isSuspendingRef.current = false;
    isReactivatingRef.current = false;
    isDeactivatingRef.current = false;
  };

  if (!user) return null;

  const isDeactivated = user.deleted_at !== null && user.deleted_at !== undefined;
  const isSuspended = user.is_suspended && !isDeactivated;

  const getStatusConfig = () => {
    if (isDeactivated) {
      return { color: '#EF4444', bg: '#FEE2E2', label: 'Deactivated', icon: AlertCircle };
    }
    if (isSuspended) {
      return { color: '#F59E0B', bg: '#FEF3C7', label: 'Suspended', icon: Clock };
    }
    if (user.is_active) {
      return { color: '#10B981', bg: '#D1FAE5', label: 'Active', icon: CheckCircle };
    }
    return { color: '#94A3B8', bg: '#F1F5F9', label: 'Pending', icon: Clock };
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`drawer-backdrop ${isOpen ? 'open' : ''}`} 
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`manage-drawer ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="drawer-header">
          <div className="drawer-header-top">
            <h2 className="drawer-title">Manage user</h2>
            <button className="drawer-close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div className="drawer-user-info">
            <div className="drawer-user-avatar">
              {(user.full_name || user.email || 'U').charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="drawer-user-name">{user.full_name || '—'}</div>
              <div className="drawer-user-email">
                <Mail size={14} />
                {user.email}
              </div>
              {user.company?.name && (
                <div className="drawer-user-company">
                  <Building2 size={14} />
                  {user.company.name}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="drawer-body">
          {/* Success Message */}
          {success && (
            <div className="drawer-success-message">
              <CheckCircle size={18} />
              {success}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="drawer-error-message">
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          {/* ✅ Show notice if user can't do anything */}
          {!canDoAnything && (
            <div className="drawer-access-notice">
              <Lock size={20} />
              <div>
                <strong>View only</strong>
                <p>You don't have permission to modify this user. Contact your administrator for access.</p>
              </div>
            </div>
          )}

          {/* Role Section - Only if canAssignRole */}
          {canAssignRole && (
            <>
              <div className="drawer-section">
                <div className="drawer-section-header">
                  <Shield size={18} className="section-icon" />
                  <h3 className="drawer-section-title">Role</h3>
                </div>
                <p className="drawer-section-subtitle">
                  Change the user's role and permissions
                </p>
                
                <div className="drawer-form-group">
                  {loadingRoles ? (
                    <div className="drawer-loading-roles">
                      <Spinner animation="border" size="sm" />
                      <span>Loading roles...</span>
                    </div>
                  ) : (
                    <>
                      <Form.Select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        className="drawer-select"
                        disabled={isSaving || isSuspended || isDeactivated}
                      >
                        <option value="">Select a role</option>
                        {roles.map((role) => (
                          <option key={role.uuid} value={role.uuid}>
                            {role.name?.replace('_', ' ').toUpperCase() || role.name}
                          </option>
                        ))}
                      </Form.Select>
                      
                      <Button
                        className="drawer-save-btn"
                        onClick={handleSaveRole}
                        disabled={isSaving || !selectedRole || selectedRole === user.role?.uuid || isSuspended || isDeactivated}
                      >
                        {isSaving ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          'Save changes'
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <div className="drawer-divider" />
            </>
          )}

          {/* Account Status Section */}
          <div className="drawer-section">
            <div className="drawer-section-header">
              <User size={18} className="section-icon" />
              <h3 className="drawer-section-title">Account status</h3>
            </div>
            
            <div className="drawer-status-display">
              <div className="drawer-status-badge" style={{ 
                background: statusConfig.bg, 
                color: statusConfig.color 
              }}>
                <StatusIcon size={14} />
                {statusConfig.label}
              </div>
              <span className="drawer-status-text">
                {isDeactivated 
                  ? 'User has been deactivated' 
                  : isSuspended 
                    ? 'User access has been revoked' 
                    : 'User currently has access to the system'}
              </span>
            </div>

            {/* ✅ Action Buttons - Only if canSuspend */}
            {canSuspend && !isDeactivated && (
              <div className="drawer-action-buttons">
                {isSuspended ? (
                  <Button
                    variant="success"
                    className="drawer-reactivate-btn"
                    onClick={() => setShowReactivateModal(true)}
                    disabled={isReactivating}
                  >
                    {isReactivating ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <RefreshCw size={16} />
                        Reactivate user
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="danger"
                    className="drawer-suspend-btn"
                    onClick={() => setShowSuspendModal(true)}
                    disabled={isSuspending || isDeactivated}
                  >
                    {isSuspending ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <Power size={16} />
                        Suspend user
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* ✅ Deactivate Section - Only if canDelete */}
          {canDelete && !isDeactivated && (
            <>
              <div className="drawer-divider" />
              
              <div className="drawer-section drawer-danger-section">
                <div className="drawer-section-header">
                  <AlertCircle size={18} className="section-icon-danger" />
                  <h3 className="drawer-section-title-danger">Deactivate account</h3>
                </div>
                <p className="drawer-section-subtitle-danger">
                  Deactivate this user. This action can be undone by an administrator.
                </p>
                
                <Button
                  variant="danger"
                  className="drawer-deactivate-btn"
                  onClick={() => setShowDeactivateModal(true)}
                  disabled={isDeactivating || isSuspended}
                >
                  {isDeactivating ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Deactivate user
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Suspend Confirmation Modal */}
      <Modal 
        show={showSuspendModal} 
        onHide={() => handleModalClose(setShowSuspendModal)}
        centered
        className="suspend-modal"
        style={{ zIndex: 99999 }}
      >
        <Modal.Header className="suspend-modal-header">
          <Modal.Title className="suspend-modal-title">
            <Power size={24} color="#EF4444" />
            Suspend user
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="suspend-modal-body">
          <p className="suspend-modal-text">
            Are you sure you want to suspend <strong>{user.full_name || user.email}</strong>?
          </p>
          <p className="suspend-modal-hint">
            This will revoke the user's access to the system. They will not be able to log in until reactivated.
          </p>
        </Modal.Body>
        <Modal.Footer className="suspend-modal-footer">
          <Button 
            variant="secondary" 
            onClick={() => handleModalClose(setShowSuspendModal)}
            disabled={isSuspending}
            className="suspend-modal-cancel"
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleSuspend}
            disabled={isSuspending}
            className="suspend-modal-confirm"
          >
            {isSuspending ? (
              <Spinner animation="border" size="sm" />
            ) : (
              'Yes, suspend user'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reactivate Confirmation Modal */}
      <Modal 
        show={showReactivateModal} 
        onHide={() => handleModalClose(setShowReactivateModal)}
        centered
        className="reactivate-modal"
        style={{ zIndex: 99999 }}
      >
        <Modal.Header className="reactivate-modal-header">
          <Modal.Title className="reactivate-modal-title">
            <RefreshCw size={24} color="#10B981" />
            Reactivate user
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="reactivate-modal-body">
          <p className="reactivate-modal-text">
            Are you sure you want to reactivate <strong>{user.full_name || user.email}</strong>?
          </p>
          <p className="reactivate-modal-hint">
            This will restore the user's access to the system.
          </p>
        </Modal.Body>
        <Modal.Footer className="reactivate-modal-footer">
          <Button 
            variant="secondary" 
            onClick={() => handleModalClose(setShowReactivateModal)}
            disabled={isReactivating}
            className="reactivate-modal-cancel"
          >
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleReactivate}
            disabled={isReactivating}
            className="reactivate-modal-confirm"
          >
            {isReactivating ? (
              <Spinner animation="border" size="sm" />
            ) : (
              'Yes, reactivate user'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Deactivate Confirmation Modal */}
      <Modal 
        show={showDeactivateModal} 
        onHide={() => handleModalClose(setShowDeactivateModal)}
        centered
        className="deactivate-modal"
        style={{ zIndex: 99999 }}
      >
        <Modal.Header className="deactivate-modal-header">
          <Modal.Title className="deactivate-modal-title">
            <Trash2 size={24} color="#EF4444" />
            Deactivate user
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="deactivate-modal-body">
          <p className="deactivate-modal-text">
            Are you sure you want to deactivate <strong>{user.full_name || user.email}</strong>?
          </p>
          <p className="deactivate-modal-hint">
            This will deactivate the user's account. They will no longer appear in the system.
          </p>
        </Modal.Body>
        <Modal.Footer className="deactivate-modal-footer">
          <Button 
            variant="secondary" 
            onClick={() => handleModalClose(setShowDeactivateModal)}
            disabled={isDeactivating}
            className="deactivate-modal-cancel"
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeactivate}
            disabled={isDeactivating}
            className="deactivate-modal-confirm"
          >
            {isDeactivating ? (
              <Spinner animation="border" size="sm" />
            ) : (
              'Yes, deactivate user'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ManageUserDrawer;